/**
 * DuviCoreAdminAppShell — the platform-admin portal frame.
 *
 * Rebuilt (epic 031, story 28) by COMPOSING the rebuilt @duvi-agency/ui shell
 * components — Sidebar (brand logo tile + filled active-nav pill), TopBar (with
 * the TOP-RIGHT user menu), Notifications, ThemeToggle and PageHeader — exactly
 * as the drawn-down platform-admin prototype's AdminApp does (screens.jsx). The
 * earlier re-wire pass had dropped the user menu, the filled active pill and the
 * brand tile; those are all restored here because they live in the shared shell
 * now. Behaviour (nav, notifications, logout, profile, mode) is re-grafted via
 * {@link useDuviCoreAdminAppShell}; this file is the composition + the UI-only
 * collapse / mobile-drawer state.
 *
 * Pages pass their PageHeader content (title/subtitle/actions/breadcrumbs) and
 * the top-bar trail; a future re-export touches only this view.
 */

import { useCallback, useState, type ReactNode } from "react";
import {
  DuviSharedAdminAppShell as SharedAppShell,
  DuviSharedAdminSidebar as Sidebar,
  DuviSharedAdminTopBar as TopBar,
  DuviSharedAdminPageHeader as PageHeader,
  DuviSharedAdminBreadcrumbs as Breadcrumbs,
  DuviSharedAdminNotifications as Notifications,
  DuviSharedAdminThemeToggle as ThemeToggle,
  DuviSharedAdminUserMenuButton as UserMenuButton,
  DuviSharedAdminDropdownMenu as DropdownMenu,
  DuviSharedAdminIconButton as IconButton,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminTestIds,
  type DuviSharedAdminBreadcrumbItem,
} from "@duvi-agency/ui";
import { useDuviCoreAdminAppShell } from "./use-duvi-core-admin-app-shell";

export interface DuviCoreAdminAppShellProps {
  children: ReactNode;
  /** Page title shown in the PageHeader. */
  title: ReactNode;
  subtitle?: ReactNode;
  /** Top-bar breadcrumb trail. Accepts plain strings or item objects. */
  breadcrumbs?: Array<string | DuviSharedAdminBreadcrumbItem>;
  /** Optional multi-level breadcrumbs rendered above the PageHeader title. */
  headerBreadcrumbs?: DuviSharedAdminBreadcrumbItem[];
  /** PageHeader actions cluster (e.g. a "New article" button). */
  headerActions?: ReactNode;
  /** Explicit active nav key; falls back to path detection. */
  activeMenuItem?: string;
}

function toCrumbItems(
  crumbs: Array<string | DuviSharedAdminBreadcrumbItem>,
): DuviSharedAdminBreadcrumbItem[] {
  return crumbs.map((c) => (typeof c === "string" ? { label: c } : c));
}

export function DuviCoreAdminAppShell({
  children,
  title,
  subtitle,
  breadcrumbs = [],
  headerBreadcrumbs,
  headerActions,
  activeMenuItem,
}: DuviCoreAdminAppShellProps) {
  const buildIcon = useCallback(
    (name: string) => <Icon name={name as never} size={18} />,
    [],
  );
  const shell = useDuviCoreAdminAppShell(buildIcon);

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeKey = shell.resolvedActiveKey(activeMenuItem);

  const sidebar = (
    <Sidebar
      brand="Duvi Platform"
      groups={shell.navGroups}
      activeKey={activeKey}
      onNavigate={(key) => {
        shell.navigateToKey(key);
        setMobileOpen(false);
      }}
      collapsed={collapsed}
      onToggleCollapse={() => setCollapsed((c) => !c)}
      open={mobileOpen}
    />
  );

  const topBar = (
    <TopBar
      search={
        <div className="flex items-center gap-3">
          <span className="hidden max-[900px]:inline-flex">
            <IconButton
              label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <Icon name="menu" size={20} />
            </IconButton>
          </span>
          <Breadcrumbs items={toCrumbItems(breadcrumbs)} />
        </div>
      }
      actions={
        <>
          <Notifications
            items={shell.notifItems}
            onItemClick={shell.handleNotifClick}
            onMarkAllRead={shell.handleMarkAllRead}
          />
          <ThemeToggle mode={shell.mode} onToggle={shell.toggleMode} />
        </>
      }
      user={
        <DropdownMenu
          align="right"
          className="min-w-[210px]"
          trigger={
            <span data-testid={DuviSharedAdminTestIds.sidebarUserMenu}>
              <UserMenuButton name={shell.profileName} role="Platform admin" />
            </span>
          }
          items={[
            {
              label: "Account settings",
              icon: <Icon name="settings" size={16} />,
              onClick: shell.handleGoToSettings,
            },
            { separator: true },
            {
              label: "Log out",
              icon: <Icon name="logout" size={16} />,
              danger: true,
              onClick: shell.handleLogout,
            },
          ]}
        />
      }
    />
  );

  return (
    <>
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-[90] hidden max-[900px]:block"
          style={{
            background: "color-mix(in srgb, var(--brand-ink) 45%, transparent)",
          }}
          aria-hidden="true"
        />
      )}
      <SharedAppShell
        sidebar={sidebar}
        topBar={topBar}
        contentClassName="max-w-content mx-auto w-full"
      >
        <PageHeader
          title={title}
          subtitle={subtitle}
          actions={headerActions}
          breadcrumbs={
            headerBreadcrumbs ? (
              <Breadcrumbs items={headerBreadcrumbs} />
            ) : undefined
          }
        />
        {children}
      </SharedAppShell>
    </>
  );
}
